import type { Aprovacao, Checkpoint, Evento, Tarefa as T } from "@escalando/contracts";
import { useState } from "react";
import { api, ErroApi } from "../api";
import { Carregando, dataHora, Situacao, useDados } from "../comum";
import type { AprovacaoVisivel } from "./Tarefas";

type Detalhe = { tarefa: T & { controle_pendente?: string }; eventos: Evento[]; checkpoints: Checkpoint[]; aprovacao?: Aprovacao };

export function Tarefa({ id }: { id: string }) {
  const det = useDados(() => api<Detalhe>("GET", `tarefas/${id}`), [id]);
  const apr = useDados(() => api<{ aprovacoes: AprovacaoVisivel[] }>("GET", "aprovacoes"), [id]);
  const [comentario, setComentario] = useState("");
  const [msg, setMsg] = useState<{ ok?: string; erro?: string }>({});

  if (!det.dados) return <Carregando erro={det.erro} />;
  const { tarefa: t, checkpoints, eventos } = det.dados;
  const pendente = apr.dados?.aprovacoes.find((a) => a.task_id === t.task_id);
  const artefatos = checkpoints.flatMap((c) => c.artifacts);

  const agir = async (f: () => Promise<unknown>, ok: string) => {
    setMsg({});
    try {
      await f();
      setMsg({ ok });
      det.recarregar();
      apr.recarregar();
    } catch (x) {
      setMsg({ erro: x instanceof ErroApi ? x.message : "falhou" });
    }
  };
  const decidir = (decision: "APROVADA" | "RECUSADA") =>
    agir(() => api("POST", `aprovacoes/${pendente?.approval_id ?? ""}`, { decision, ...(comentario ? { comentario } : {}) }), decision === "APROVADA" ? "Decisão registrada." : "Recusa registrada.");

  return (
    <>
      <a href="#/tarefas" className="suave">← Tarefas</a>
      <h1>{t.title}</h1>
      <div className="linha"><Situacao s={t.status} /><span className="suave">{t.project_id} · {t.specialist} · tentativa {t.attempts}/{t.max_attempts}</span></div>
      {t.description && <pre>{t.description}</pre>}
      {t.diagnostico && <p className="erro">{t.diagnostico}</p>}

      {pendente && (
        <div className="cartao" style={{ borderColor: "var(--cor-alerta)" }}>
          <strong>{pendente.kind === "AUTORIZAR_ACAO" ? `Autorizar ação ${pendente.action_type ?? ""}` : "Aprovar resultado"}</strong>
          <pre>{pendente.resumo}</pre>
          {pendente.target && <p className="suave">Alvo: {pendente.target}</p>}
          <p className="suave">Expira {dataHora(pendente.expires_at)}</p>
          {pendente.pode_decidir ? (
            <>
              <label htmlFor="c">Comentário (opcional; numa recusa, diz o que mudar)</label>
              <textarea id="c" value={comentario} onChange={(e) => setComentario(e.target.value)} style={{ minHeight: 60 }} />
              <div className="linha" style={{ marginTop: 8 }}>
                <button className="sucesso" onClick={() => void decidir("APROVADA")}>{pendente.kind === "AUTORIZAR_ACAO" ? "Autorizar ação" : "Aprovar resultado"}</button>
                <button className="perigo" onClick={() => void decidir("RECUSADA")}>Recusar</button>
              </div>
            </>
          ) : <p className="suave">Esta decisão é de outra pessoa.</p>}
        </div>
      )}

      <div className="linha">
        {["READY", "CLAIMED", "RUNNING"].includes(t.status) && <button onClick={() => void agir(() => api("POST", `tarefas/${t.task_id}/pausar`, {}), "Pausa pedida.")}>Pausar</button>}
        {["PAUSED", "BLOCKED", "FAILED"].includes(t.status) && <button onClick={() => void agir(() => api("POST", `tarefas/${t.task_id}/retomar`, {}), "Tarefa de volta à fila.")}>Retomar</button>}
        {!["DONE", "FAILED", "CANCELLED"].includes(t.status) && <button className="perigo" onClick={() => void agir(() => api("POST", `tarefas/${t.task_id}/cancelar`, {}), "Tarefa cancelada.")}>Cancelar</button>}
      </div>
      {msg.ok && <p className="ok">{msg.ok}</p>}
      {msg.erro && <p className="erro">{msg.erro}</p>}

      {artefatos.length > 0 && (
        <>
          <h2>Entregas</h2>
          {artefatos.map((a) => <div key={a.chave} className="cartao">{a.chave.split("/").pop()} <span className="suave">({Math.ceil(a.bytes / 1024)} KB)</span></div>)}
        </>
      )}
      <h2>Andamento</h2>
      {checkpoints.length === 0 && eventos.length === 0 && <p className="suave">Ainda não começou.</p>}
      {eventos.slice(-15).reverse().map((e) => (
        <div key={e.seq} className="suave">{dataHora(e.created_at)} · {e.type}{typeof e.payload["etapa"] === "string" ? ` (${e.payload["etapa"]})` : ""}</div>
      ))}
    </>
  );
}
